import React, { useEffect, useState, useCallback } from 'react';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { compose } from 'recompose';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { debounce } from 'lodash';
import { StoreState } from '@mm/state/store-interfaces';
import campaignsCRUDActions from '@mm/state/actions/CampaignsCRUDActions';
import {
  Grid,
  GridCell,
  GridLabels,
} from '../../../../../../common/components/Grid/Grid';
import { ContactRow } from './ContactRow/ContactRow';
import { PaginationActions } from './PaginationActions/PaginationActions';
import './CampaignQueueModal.scss';
import Spinner from '../../../../../../common/components/Spinner';
import classnames from 'classnames';
import {
  CampaignType,
  QueuedContactsType,
} from '@mm/state/model/CampaignsModel';
import { usePrevious } from '../../../../../../common/hooks/use-previous';

type OuterProps = {
  isOpen: boolean;
  onClose: () => void;
  onFetch: (count: number) => void;
  campaign: CampaignType;
  schedulerId: number;
};

type InnerProps = {
  fetchQueuedContacts: (
    campaignId: number,
    schedulerId: number,
    page: number,
    perPage: number,
  ) => QueuedContactsType;
} & ReturnType<typeof mapStateToProps>;

type Props = InnerProps & OuterProps;

const mapDispatchToProps = (dispatch: Dispatch<StoreState>) => ({
  fetchQueuedContacts: (
    campaignId: number,
    schedulerId: number,
    page: number,
    perPage: number,
  ) =>
    dispatch(
      campaignsCRUDActions.fetchQueuedContacts(
        campaignId,
        schedulerId,
        page,
        perPage,
      ),
    ),
});

const mapStateToProps = (state: StoreState, { schedulerId }: OuterProps) => {
  const queuedContacts = state.campaigns.queuedContacts[schedulerId];
  return {
    contacts: (queuedContacts && queuedContacts.contacts) || [],
    pagination: (queuedContacts && queuedContacts.pagination) || {},
  };
};

const enhance = compose<InnerProps, OuterProps>(
  connect(mapStateToProps, mapDispatchToProps),
);

const CampaignQueueModal = (props: Props) => {
  const {
    isOpen,
    onClose,
    contacts,
    pagination,
    fetchQueuedContacts,
    campaign,
    schedulerId,
    onFetch,
  } = props;
  const [pageNumber, setPageNumber] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [fetching, setFetching] = useState(false);
  const modalClass = classnames('campaign-queue-modal', {
    'campaign-queue-modal--loading': fetching,
    'campaign-queue-modal--empty': contacts && contacts.length === 0,
  });

  const fetch = async (page: number, contactsPerPage: number) => {
    setFetching(true);
    const response: QueuedContactsType = await fetchQueuedContacts(
      campaign.id,
      schedulerId,
      page,
      contactsPerPage,
    );
    onFetch(response.pagination.total);
    setFetching(false);
  };

  const debouncedFetch = useCallback(
    debounce(fetch, 5000, { leading: true, trailing: false }),
    [campaign?.id, schedulerId, fetchQueuedContacts, onFetch],
  );
  const previousDebouncedFetch = usePrevious(debouncedFetch);

  useEffect(() => {
    previousDebouncedFetch?.cancel();
  }, [debouncedFetch]);

  const fetchContacts = (
    page: number,
    contactsPerPage: number,
    force: boolean = false,
  ) => {
    if (force) {
      fetch(page, contactsPerPage);
    } else {
      debouncedFetch(page, contactsPerPage);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchContacts(pageNumber, perPage);
    }
  }, [isOpen]);

  const onChangePage = async (page: number) => {
    setPageNumber(page);
    await fetchContacts(page, perPage, true);
  };

  const onChangePerPage = async (contactsPerPage: number) => {
    setPerPage(contactsPerPage);
    await fetchContacts(pageNumber, contactsPerPage, true);
  };

  return (
    <Modal className={modalClass} isOpen={isOpen} toggle={onClose}>
      <ModalHeader className="campaign-queue-modal__header" toggle={onClose}>
        Subscribers in queue
      </ModalHeader>
      <ModalBody className="campaign-queue-modal__content">
        {fetching && (
          <Spinner
            show={true}
            position="absolute"
            backgroundColor="none"
            size={30}
          />
        )}
        {!fetching && contacts.length > 0 && (
          <>
            <PaginationActions
              pagination={{
                ...pagination,
                isLastPage: pageNumber === pagination.totalPages,
              }}
              onChangePage={onChangePage}
              onChangePerPage={onChangePerPage}
              page={pageNumber}
              perPage={perPage}
            />
            <div className="campaign-queue-modal__contacts">
              <div className="campaign-queue-modal__contacts-container">
                <Grid className="campaign-queue-modal">
                  <GridLabels className="campaign-queue-modal__grid_cells">
                    <GridCell>
                      <span>Name</span>
                    </GridCell>
                    <GridCell>
                      <span>Scheduled to receive message</span>
                    </GridCell>
                  </GridLabels>
                  {contacts.map((contact, index) => (
                    <ContactRow contact={contact} key={index} />
                  ))}
                </Grid>
              </div>
            </div>
          </>
        )}
        {!fetching && contacts.length === 0 && (
          <div className="campaign-queue-modal__empty-contacts">
            No queued contacts left for the campaign
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

export default enhance(CampaignQueueModal);
